import { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import axiosRetry from "axios-retry";
import { AlertConstants, useAlerts } from "best-common-react";
import React from "react";
import { LocalStorageConstants } from "../constants/LocalStorageConstants";
import { useUser } from "../contexts/UserContext";
import { IPLAlert } from "../types/Alert";
import AuthClient from "./AuthClient";

interface HttpInterceptorProps {
  children: React.ReactNode;
}

let interceptorId = 0;

const HttpInterceptor: React.FC<HttpInterceptorProps> = ({ children }) => {
  const { logout } = useUser();
  const { addAlert } = useAlerts();

  const createResponseAlerts = (alerts: IPLAlert[]) => {
    alerts.forEach((alert: IPLAlert) => {
      addAlert({
        type: !!alert.severity ? alert.severity : AlertConstants.TYPES.WARNING,
        text: !!alert.message ? alert.message : "There was an error please contact support",
      });
    });
  };

  axiosRetry(AuthClient, {
    retries: 3, //attempts to retry
    retryCondition: (error) => {
      console.log("Error fetching from API will retry...");
      return !!error?.response && (error.response.status === 401 || error.response.status === 403);
    }, // only retry on unauthenticated errors to prevent retrying 500s unless that is wanted
    retryDelay: (retryCount) => retryCount * 1000, // wait 1 second between retrys
  });

  AuthClient.interceptors.request.use((config: AxiosRequestConfig) => {
    const oktaTokenStr =
      localStorage.getItem(LocalStorageConstants.OKTA_TOKEN) || '{"accessToken": "", "idToken": "" }';

    const { accessToken = {}, idToken = {} } = JSON.parse(oktaTokenStr);

    if (config.url.indexOf("/oauth/token") < 0 || config.headers.addToken) {
      config.headers.Authorization = `Bearer ${accessToken.accessToken}`;
      config.headers["x-id-token"] = idToken.idToken;
    }
    return config;
  });

  if (!!interceptorId || interceptorId === 0) {
    AuthClient.interceptors.response.eject(interceptorId);
  }

  interceptorId = AuthClient.interceptors.response.use(
    (resp: AxiosResponse) => {
      if (!resp) {
        return null;
      } else if (resp.data && resp.data.alerts) {
        createResponseAlerts(resp.data.alerts);
      }
      return resp;
    },
    (err: AxiosError) => {
      if (!!err.response && (err.response.status === 401 || err.response.status === 403)) {
        logout();
      } else if (!!err?.response?.data?.alerts) {
        createResponseAlerts(err.response.data.alerts);
      } else if (!!err?.message) {
        addAlert({
          type: AlertConstants.TYPES.DANGER,
          text: err.message,
        });
      } else {
        addAlert({
          type: AlertConstants.TYPES.DANGER,
          text: "There was an error with IPL please try again or contact support",
        });
      }
      return Promise.reject(err);
    },
  );

  return <>{children}</>;
};

export default HttpInterceptor;
